/**
 * DENET — hero slider
 * Geçiş logodaki çubuk motifiyle yapılır: gelen slaydın üstünü kaplayan
 * lacivert çubuklar sırayla açılır.
 *
 * Kurallar:
 * - Otomatik geçiş imleç, odak veya sekme gizlendiğinde durur.
 * - `prefers-reduced-motion` açıkken perde ve dolum animasyonu çalışmaz,
 *   slaytlar yine de gezilebilir.
 * - Klavye: sol/sağ ok. Dokunmatik: yatay kaydırma.
 */

import { DenetElement, define, qsa, media } from '@denet/core';

const SWIPE_THRESHOLD = 45;

class DnHeroSlider extends DenetElement {
  #index = 0;
  /** @type {number | undefined} */
  #timer;
  /** @type {number | undefined} */
  #wipeTimer;
  /** @type {(() => void) | undefined} */
  #wipeDone;
  #paused = false;

  mounted() {
    this.slides = /** @type {HTMLElement[]} */ (qsa('[data-slide]', this));
    this.dots = /** @type {HTMLElement[]} */ (qsa('[data-dot]', this));
    this.wipe = this.querySelector('[data-ref="wipe"]');
    this.usesWipe = this.dataset.transition === 'slats' && Boolean(this.wipe);
    this.counter = this.querySelector('[data-ref="counter"]');
    this.prevButton = this.querySelector('[data-hero-prev]');
    this.nextButton = this.querySelector('[data-hero-next]');

    if (this.slides.length < 2) {
      this.slides[0]?.classList.add('is-active');
      return;
    }

    this.interval = (Number(this.dataset.interval) || 7) * 1000;
    this.#show(0, { instant: true });

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.#goTo(i), { signal: this.signal });
    });

    /* İleri/geri: dairesel — son slayttan sonra başa döner, o yüzden hiçbir
       zaman devre dışı kalmazlar. */
    this.prevButton?.addEventListener('click', () => this.#goTo(this.#index - 1), { signal: this.signal });
    this.nextButton?.addEventListener('click', () => this.#goTo(this.#index + 1), { signal: this.signal });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.#goTo(this.#index + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.#goTo(this.#index - 1); }
    }, { signal: this.signal });

    // Duraklatma: okurken slayt kaymamalı.
    for (const [evt, paused] of [['pointerenter', true], ['pointerleave', false], ['focusin', true], ['focusout', false]]) {
      this.addEventListener(/** @type {string} */ (evt), () => this.#setPaused(Boolean(paused)), { signal: this.signal });
    }

    document.addEventListener('visibilitychange', () => this.#setPaused(document.hidden), { signal: this.signal });

    this.#initSwipe();
    this.#start();
  }

  unmounted() {
    window.clearTimeout(this.#timer);
    window.clearTimeout(this.#wipeTimer);
  }

  /* ------------------------------------------------------------- zamanlama */

  #start() {
    if (this.#paused || media.reducedMotion) return;
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#goTo(this.#index + 1), this.interval);
  }

  /** @param {boolean} value */
  #setPaused(value) {
    this.#paused = value;
    this.classList.toggle('is-paused', value);
    if (value) window.clearTimeout(this.#timer);
    else this.#start();
  }

  /* ---------------------------------------------------------------- geçiş */

  /** @param {number} next */
  #goTo(next) {
    const total = this.slides.length;
    const target = ((next % total) + total) % total;
    if (target === this.#index) return;
    this.#show(target);
  }

  /**
   * @param {number} target
   * @param {{ instant?: boolean }} [options]
   */
  #show(target, options = {}) {
    const previous = this.slides[this.#index];
    const slide = this.slides[target];
    if (!slide) return;

    this.#index = target;

    this.slides.forEach((el, i) => {
      el.classList.toggle('is-active', i === target);
      el.setAttribute('aria-hidden', String(i !== target));
    });

    if (this.counter) this.counter.textContent = String(target + 1);

    this.dots.forEach((dot, i) => {
      const active = i === target;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', String(active));
      // Dolum animasyonunu yeniden başlatmak için sınıf sıfırlanır.
      dot.classList.remove('is-timing');
      if (active && !options.instant) void dot.offsetWidth;
      if (active) dot.classList.add('is-timing');
    });

    /* Yumuşak geçişte iki panel birlikte çapraz geçer; çıkan paneli zorla
       görünür tutmak gerekmez — tutulursa perde bittiğinde sert kesiliyordu.
       Çubuk perdede ise çıkan panel perdenin arkasında görünür kalmalı, yoksa
       şeritlerin arasından bir an boşluk görünür. */
    if (!options.instant && previous && previous !== slide && this.usesWipe) {
      previous.classList.add('is-leaving');
      this.#runWipe(() => previous.classList.remove('is-leaving'));
    }

    this.#start();
  }

  /** @param {() => void} done */
  #runWipe(done) {
    if (!this.wipe || media.reducedMotion) { done(); return; }

    /* Hızlı tıklamada ikinci geçiş birincinin ortasında başlıyordu: eski
       zamanlayıcı perdeyi yarı yolda kaldırıp önceki slaydı bir an açığa
       çıkarıyordu. Bekleyen iş varsa önce kapatılır. */
    window.clearTimeout(this.#wipeTimer);
    this.#wipeDone?.();

    const bars = this.wipe.children.length;
    const duration = 400 + bars * 45;

    this.#wipeDone = done;

    this.wipe.classList.remove('is-open');
    this.wipe.classList.add('is-running');
    // Yeniden akış: sınıf kaldırılıp eklendiğinde tarayıcı geçişi yeniden kurar.
    void /** @type {HTMLElement} */ (this.wipe).offsetWidth;
    this.wipe.classList.add('is-open');

    this.#wipeTimer = window.setTimeout(() => {
      this.wipe?.classList.remove('is-running', 'is-open');
      this.#wipeDone?.();
      this.#wipeDone = undefined;
    }, duration);
  }

  /* -------------------------------------------------------------- dokunma */

  #initSwipe() {
    let startX = 0;
    let startY = 0;
    let tracking = false;

    this.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      tracking = true;
    }, { passive: true, signal: this.signal });

    this.addEventListener('touchend', (event) => {
      if (!tracking) return;
      tracking = false;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      // Dikey kaydırma sayfayı gezdirmeli; yalnızca belirgin yatay hareket sayılır.
      if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
      this.#goTo(this.#index + (dx < 0 ? 1 : -1));
    }, { passive: true, signal: this.signal });
  }
}

define('dn-hero-slider', DnHeroSlider);
